import type { Role } from '@smarton-pbx/shared';
import { Check, Download, Settings2 } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { api } from '../api';
import { t } from '../i18n';
import { Menu } from './ui';

/** What an Excel cell holds: text, a number, yes/no or a time (shown as the browser's clock). */
export type ExcelValue = string | number | boolean | Date | null | undefined;

export interface ExcelColumn<T> {
  label: string;
  value: (row: T) => ExcelValue;
  format?: string;
}

export interface Column<T> {
  /** Stable: remembered in the browser, and named in the audit log. */
  key: string;
  /** The heading, on the screen and in the Excel file. */
  label: string;
  cell: (row: T) => ReactNode;
  /** The cell in the Excel file; without it (or `excel`) the column isn't in the file. */
  value?: (row: T) => ExcelValue;
  /** Instead: several Excel columns, when the cell shows several things (a name, and the email under it). */
  excel?: ExcelColumn<T>[];
  /** Excel's format for a time column, when not the date and time. */
  excelFormat?: string;
  /** On the heading and every cell (hide-mobile, tabular…). */
  className?: string;
  /** Always shown (what names the row), so not in the list to hide. */
  fixed?: boolean;
  /** Hidden until the person shows it. */
  hidden?: boolean;
  /** A column of icons: its heading only for screen readers (the label still names it in the ⚙ menu). */
  quietHeading?: boolean;
  /** Explains the heading on hover. */
  hint?: string;
}

/** Who may download tables: like the pipeline's export. */
export const mayDownload = (role: Role) => role === 'TENANT_ADMIN' || role === 'REGION_MANAGER';

export interface Table<T> {
  /** The columns to show, in order. */
  columns: Column<T>[];
  /** The ⚙ menu, for the heading row's last cell. */
  settings: ReactNode;
}

/** The heading row: the shown columns, then the ⚙ menu above the rows' actions. */
export function TableHead<T>({ table }: { table: Table<T> }) {
  return (
    <thead>
      <tr>
        {table.columns.map((c) =>
          c.quietHeading ? (
            <th key={c.key} className={c.className} aria-label={c.label} />
          ) : (
            <th key={c.key} className={c.className} title={c.hint}>
              {c.label}
            </th>
          ),
        )}
        <th className="actions">{table.settings}</th>
      </tr>
    </thead>
  );
}

/** A row's shown cells; the row adds its actions cell (empty if it has none). */
export function Cells<T>({ table, row }: { table: Table<T>; row: T }) {
  return (
    <>
      {table.columns.map((c) => (
        <td key={c.key} className={c.className}>
          {c.cell(row)}
        </td>
      ))}
    </>
  );
}

type Choices = Record<string, boolean>;

function readChoices(id: string): Choices {
  try {
    const saved = localStorage.getItem(`columns:${id}`);
    return saved ? (JSON.parse(saved) as Choices) : {};
  } catch {
    return {};
  }
}

/**
 * A table's columns as the person chose them (remembered in this browser),
 * and its ⚙ menu: the columns to show, and for admins and managers the
 * download of the rows, with the shown columns, as an Excel file. `rows`
 * gives every row the table has (the filtered ones), not only those loaded.
 */
export function useTable<T>(id: string, all: Column<T>[], download?: { name: string; rows: () => T[] | Promise<T[]> } | null): Table<T> {
  const [choices, setChoices] = useState<Choices>(() => readChoices(id));
  const visible = (column: Column<T>) => Boolean(column.fixed) || (choices[column.key] ?? !column.hidden);
  const columns = all.filter(visible);

  function toggle(column: Column<T>) {
    const next = { ...choices, [column.key]: !visible(column) };
    setChoices(next);
    try {
      localStorage.setItem(`columns:${id}`, JSON.stringify(next));
    } catch {
      // Not kept (private window): still applies on this page.
    }
  }

  const settings = (
    <TableSettings
      columns={all.filter((c) => !c.fixed)}
      visible={visible}
      toggle={toggle}
      download={download ? () => downloadExcel(id, download.name, columns, download.rows) : null}
    />
  );
  return { columns, settings };
}

function TableSettings<T>({
  columns,
  visible,
  toggle,
  download,
}: {
  columns: Column<T>[];
  visible: (column: Column<T>) => boolean;
  toggle: (column: Column<T>) => void;
  download: (() => Promise<void>) | null;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();

  async function run(close: () => void) {
    setBusy(true);
    setError(undefined);
    try {
      await download!();
      close();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Menu
      label={t('Table settings')}
      trigger={(props) => (
        <button type="button" className="ghost icon" aria-label={t('Table settings')} title={t('Table settings')} {...props} data-testid="table-settings">
          <Settings2 size={16} aria-hidden />
        </button>
      )}
    >
      {(close) => (
        <>
          {columns.length > 0 && <div className="menu-heading">{t('Columns')}</div>}
          {columns.map((column) => (
            <button
              key={column.key}
              type="button"
              role="menuitemcheckbox"
              aria-checked={visible(column)}
              onClick={() => toggle(column)}
              data-testid={`column-${column.key}`}
            >
              <Check size={16} aria-hidden style={{ visibility: visible(column) ? 'visible' : 'hidden' }} /> {column.label}
            </button>
          ))}
          {download && (
            <>
              {columns.length > 0 && <hr />}
              <button type="button" role="menuitem" disabled={busy} onClick={() => void run(close)} data-testid="download-excel">
                <Download size={16} aria-hidden /> {busy ? t('Preparing…') : t('Download Excel')}
              </button>
              {error && <div className="menu-error">{error}</div>}
            </>
          )}
        </>
      )}
    </Menu>
  );
}

/** Excel has no time zones: a time as this browser's clock shows it. */
const asShown = (time: Date) => new Date(time.getTime() - time.getTimezoneOffset() * 60_000);

const pad = (n: number) => String(n).padStart(2, '0');
const today = () => {
  const now = new Date();
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
};

/** The rows, with the shown columns that have a value, as `{table}-{date}.xlsx`; noted in the audit log first. */
async function downloadExcel<T>(id: string, name: string, columns: Column<T>[], load: () => T[] | Promise<T[]>): Promise<void> {
  const shown = columns.filter((c) => c.value || c.excel);
  const exported: ExcelColumn<T>[] = shown.flatMap(
    (c) =>
      c.excel ?? [
        {
          label: c.label,
          value: c.value!,
          ...(c.excelFormat && { format: c.excelFormat }),
        },
      ],
  );
  const [rows, { default: writeXlsxFile }] = await Promise.all([load(), import('write-excel-file/browser')]);
  await api.tableExported({
    table: id,
    rows: rows.length,
    columns: shown.map((c) => c.key),
  });
  const cell = (value: ExcelValue, format?: string) =>
    value instanceof Date ? { value: asShown(value), format: format ?? 'yyyy-mm-dd hh:mm' } : format && typeof value === 'number' ? { value, format } : (value ?? null);
  const body = rows.map((row) => exported.map((c) => cell(c.value(row), c.format)));
  // Wide enough for the longest value, within reason.
  const widths = exported.map((c, i) =>
    Math.min(
      60,
      Math.max(
        10,
        c.label.length + 2,
        ...body.map((r) => {
          const v = r[i];
          return v === null ? 0 : typeof v === 'object' ? 16 : String(v).length + 2;
        }),
      ),
    ),
  );
  await writeXlsxFile([exported.map((c) => ({ value: c.label, fontWeight: 'bold' as const })), ...body], {
    // Excel's limits for a sheet's name.
    sheet: name.replace(/[[\]:*?/\\]/g, ' ').slice(0, 31),
    stickyRowsCount: 1,
    columns: widths.map((width) => ({ width })),
  }).toFile(`${id}-${today()}.xlsx`);
}
